import { ChangeEvent, CSSProperties, ReactNode, useEffect, useState } from "react";

import { Maybe } from "@/frameworks/libraries/components";

import * as S from "./emotion";
import { Typography } from "../Typography";
import { Checkbox } from "../Inputs";

export interface TableHeaderInterface {
	id: string;
	label: string | React.JSX.Element;
	hasCheckBox?: boolean;
}

export interface TableProps {
	tableHeaders?: TableHeaderInterface[];
	children: ReactNode;
}

type LockableItem = {
	locked: boolean;
	authenticated: boolean;
};

export default function DataTable<T>({
	tableHeaders,
	data,
	hasCheckBox,
	tdList,
	setCheckedList,
	onClickRow,
	thStyle,
	tdStyle,
	tbStyle
}: {
	tableHeaders: TableHeaderInterface[];
	data?: T[];
	hasCheckBox?: boolean;
	tdList: (rowData: T) => Partial<Record<keyof T & TableHeaderInterface["id"], React.JSX.Element>>;
	setCheckedList?: React.Dispatch<React.SetStateAction<T[]>>;
	onClickRow?: (data: T) => void;
	thStyle?: CSSProperties;
	tdStyle?: CSSProperties;
	tbStyle?: CSSProperties;
}) {
	const [selectedData, setSelectedData] = useState<T[]>([]);
	const [isAllChecked, setIsAllChecked] = useState(false);

	useEffect(() => {
		setIsAllChecked(false);
	}, [data]);

	const selectAll = () => {
		setIsAllChecked((prev) => !prev);
		if (data && setCheckedList) {
			if (isAllChecked) {
				setCheckedList([]);
				setSelectedData([]);
			} else {
				const filteredData = (data as (T & LockableItem)[]).filter(
					(item) => item.locked || !item.authenticated
				);
				setCheckedList((prev) => [...new Set([...prev, ...filteredData])]);
				setSelectedData((prev) => [...new Set([...prev, ...filteredData])]);
			}
		}
	};
	const handleCheck = (ev: ChangeEvent<HTMLInputElement>, data: T) => {
		ev.stopPropagation();
		const { checked } = ev.currentTarget;
		if (!setCheckedList) {
			return;
		}
		if (checked) {
			setCheckedList((prev) => [...prev, data]);
			setSelectedData((prev) => [...prev, data]);
		} else {
			setCheckedList((prev) => prev.filter((e) => e !== data));
			setSelectedData((prev) => prev.filter((e) => e !== data));
		}
	};

	const getIsDisabled = (row: T & LockableItem) => {
		return row.authenticated && !row.locked;
	};

	return (
		<S.StyledTable
			style={{ borderStyle: "hidden", width: "100%", height: "100%", borderCollapse: "separate", ...tbStyle }}
		>
			<thead>
				<S.TableHeader style={thStyle}>
					<Maybe test={hasCheckBox}>
						<S.StyledTh key={"checkbox"} style={{ whiteSpace: "nowrap" }}>
							<Checkbox onChange={selectAll} checked={isAllChecked} />
						</S.StyledTh>
					</Maybe>
					{tableHeaders?.map((header) => (
						<S.StyledTh key={header.id as string} style={{ whiteSpace: "nowrap" }}>
							<Maybe test={typeof header.label === "string"} fallback={header.label}>
								<Typography typoSize="Body3_MD">{header.label}</Typography>
							</Maybe>
						</S.StyledTh>
					))}
				</S.TableHeader>
			</thead>
			<tbody style={{ display: "table-row-group" }}>
				{data?.map((row, rowIndex) => (
					<S.StyledTr
						key={rowIndex}
						selected={selectedData.includes(row)}
						onClick={(ev) => {
							ev.stopPropagation();
							onClickRow?.(row);
						}}
					>
						{hasCheckBox && (
							<S.StyledTd key={rowIndex} onClick={(ev) => ev.stopPropagation()}>
								<div style={{ width: "fit-content" }}>
									<Checkbox
										onChange={(ev) => {
											handleCheck(ev, row);
										}}
										checked={selectedData.includes(row)}
										disabled={getIsDisabled(row as T & LockableItem)}
									/>
								</div>
							</S.StyledTd>
						)}
						{tableHeaders?.map((header, index) => (
							<S.StyledTd key={index} style={{ whiteSpace: "nowrap", ...tdStyle }}>
								{tdList(row)[header.id as keyof T]}
							</S.StyledTd>
						))}
					</S.StyledTr>
				))}
			</tbody>
		</S.StyledTable>
	);
}
